import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from '@angular/core';
import { EnumResponse } from "@core/models/enum-response";
import { environment } from "@environments/environment";
import { firstValueFrom, Observable } from "rxjs";

@Injectable({
    providedIn: 'root'
})
export class EnumsService {
    private uri = environment.api.uri;
    private initPromise: Promise<void> | null = null;
    private http = inject(HttpClient)

    public superintendencias: EnumResponse[] = [];
    public concessionarias: EnumResponse[] = [];
    public motivos: EnumResponse[] = [];
    public status: EnumResponse[] = [];
    public fases: EnumResponse[] = [];
    public tiposTap: EnumResponse[] = [];
    public situacoesTap: EnumResponse[] = [];
    public grupos: EnumResponse[] = [];
    public niveis: EnumResponse[] = [];
    public cursos: EnumResponse[] = [];
    public rodovias: EnumResponse[] = [];
    public diretorias: EnumResponse[] = [];

    getSuperintendenciaEnum(): Observable<EnumResponse[]> {
        return this.http.get<EnumResponse[]>(`${this.uri}superintendencia`);
    }

    getStatusEnum(): Observable<EnumResponse[]> {
        return this.http.get<EnumResponse[]>(`${this.uri}status`);
    }

    getSituacaoTapEnum(): Observable<EnumResponse[]> {
        return this.http.get<EnumResponse[]>(`${this.uri}situacao-tap`);
    }

    getMotivoEnum(): Observable<EnumResponse[]> {
        return this.http.get<EnumResponse[]>(`${this.uri}motivo`);
    }

    getDiretoriaEnum(): Observable<EnumResponse[]> {
        return this.http.get<EnumResponse[]>(`${this.uri}diretoria`);
    }

    getFaseEnum(): Observable<EnumResponse[]> {
        return this.http.get<EnumResponse[]>(`${this.uri}fase`);
    }

    getCursoEnum(): Observable<EnumResponse[]> {
        return this.http.get<EnumResponse[]>(`${this.uri}curso`);
    }

    getConcessionariaEnum(): Observable<EnumResponse[]> {
        return this.http.get<EnumResponse[]>(`${this.uri}concessionaria`);
    }

    getTipoTapEnum(): Observable<EnumResponse[]> {
        return this.http.get<EnumResponse[]>(`${this.uri}tipo-tap`);
    }

    getNivelEnum(): Observable<EnumResponse[]> {
      return this.http.get<EnumResponse[]>(`${this.uri}nivel`);
    }

    getGrupoEnum(): Observable<EnumResponse[]> {
        return this.http.get<EnumResponse[]>(`${this.uri}grupo`);
    }

    public getObjectFromValue(options: EnumResponse[], value: any): EnumResponse | undefined {
        if (!options || value === null || value === undefined) {
            return undefined;
        }
        return options.find(option => option.id === value) || undefined;
    }

    public getValueFromEnum(value: string | EnumResponse | undefined): any {
        if (!value) {
            return null;
        }
        return typeof value === 'object' ? value.id : value;
    }

    public getLabelFromEnum(value: string | EnumResponse | undefined): any {
        if (!value) {
            return null;
        }
        return typeof value === 'object' ? value.nome : value;
    }


    private fetchEnum(endpoint: string): Observable<EnumResponse[]> {
        return this.http.get<EnumResponse[]>(`${this.uri}${endpoint}`);
    }

    /**
     * Atualiza opções locais comparando com backend.
     * Se backend for diferente, sobrescreve.
     */
    private updateOptions(
        localOptions: EnumResponse[],
        backendOptions: EnumResponse[]
    ): EnumResponse[] {
        // transforma em map para comparação mais confiável
        const localMap = new Map(localOptions.map(o => [o.id, o.nome]));
        const backendMap = new Map(backendOptions.map(o => [o.id, o.nome]));

        // se quantidade for diferente, substitui
        if (localMap.size !== backendMap.size) {
            console.info("[EnumsService] Substituindo opções (tamanho diferente)");
            return backendOptions.map(o => ({id: o.id, nome: o.nome}));
        }

        // compara item a item ignorando ordem
        for (const [id, nome] of backendMap.entries()) {
            if (!localMap.has(id) || localMap.get(id) !== backendMap.get(id)) {
                console.info("[EnumsService] Substituindo opções (conteúdo diferente)");
                return backendOptions.map(o => ({id: o.id, nome: o.nome}));
            }
        }
        console.info("[EnumsService] Enums sem alteração.");
        return localOptions;
    }

    private replaceOptions(
        target: EnumResponse[],
        backendOptions: EnumResponse[]
    ) {
        const updated = this.updateOptions(target, backendOptions);
        target.splice(0, target.length, ...updated); // mantém a referência e substitui valores
    }

    private setMockEnums() {
        const mk = (names: string[]): EnumResponse[] => names.map((nome, i) => ({id: i + 1, nome}));
        this.superintendencias.push(...mk(['SUROD']));
        this.diretorias.push(...mk(['DIN', 'DOP', 'SUROD']));
        this.concessionarias.push(...mk([
            '01 - AUTOBAN', '03 - TEBE', '05 - VIANORTE', '06 - INTERVIAS',
            '07 - ROTA DAS BANDEIRAS', '08 - CENTROVIAS', '09 - TRIÂNGULO DO SOL',
            '10 - AUTOVIAS', '11 - RENOVIAS', '12 - VIAOESTE', '13 - COLINAS',
            '16 - CART', '19 - VIARONDON', '20 - SPVIAS', '21 - RODOVIAS DO TIETÊ',
            '22 - ECOVIAS IMIGRANTES', '23 - ECOVIAS LESTE PAULISTA', '24 - RODOANEL OESTE',
            '25 - SPMAR', '27 - RODOVIA DOS TAMOIOS', '28 - ENTREVIAS', '29 - VIAPAULISTA',
            '30 - EIXO SP', '31 - ECOVIAS NOROESTE PAULISTA', '33 - ROTA SOROCABANA',
            'VOA SE', 'VOA SP'
        ]));
        this.motivos.push(...mk(['NC', 'NE', 'NI']));
        this.status.push(...mk(['TAP', 'EM ANÁLISE', 'ARBITRAL', 'ACOLHIDA', 'CANCELADA', 'ANULADA']));
        this.fases.push(...mk(['AAF', 'ADP', 'AF', 'ARE', 'CRJ', 'DI', 'DP', 'JUD', 'TAP']));
        this.tiposTap.push(...mk(['ADVERTÊNCIA', 'MULTA']));
        this.situacoesTap.push(...mk(['ANULADA', 'COBRANÇA', 'JUD', 'PRAZ', 'PRAZO PGTO', 'REC JUD', 'VENCIDA']));
        this.cursos.push(...mk(['DEL', 'Ordinário', 'QNL']));
        this.grupos.push(...mk([]));
        this.niveis.push(...mk([]));
        console.info('[EnumsService] Enums locais carregados (modo local).');
    }

    /**
     * Carrega os enums uma única vez. Chamadas concorrentes recebem a MESMA
     * promise em andamento — assim ninguém segue em frente com as listas ainda vazias.
     */
    initEnums(): Promise<void> {
      if (!this.initPromise) {
          this.initPromise = this.loadEnums().catch(error => {
              console.error('[EnumsService] Erro inesperado ao carregar enums.', error);
              this.initPromise = null; // permite nova tentativa após falha total
          });
      }
      return this.initPromise;
    }

    private async loadEnums(): Promise<void> {
      if ((environment as any).local) {
          this.setMockEnums();
          return;
      }

      console.info("[EnumsService] Iniciado verificação de Enums no backend.");

      const enumNames = [
          'superintendencia',
          'concessionaria',
          'motivo',
          'status',
          'fase',
          'tipo-tap',
          'situacao-tap',
          'curso',
          'diretoria',
          'grupo',
          'nivel'
      ];

      const targets = [
          this.superintendencias,
          this.concessionarias,
          this.motivos,
          this.status,
          this.fases,
          this.tiposTap,
          this.situacoesTap,
          this.cursos,
          this.diretorias,
          this.grupos,
          this.niveis
      ];

      try {
          const results = await Promise.allSettled(
              enumNames.map(name => firstValueFrom(this.fetchEnum(name)))
          );

          results.forEach((result, index) => {
              if (result.status === 'fulfilled') {
                  this.replaceOptions(targets[index], result.value);
              } else {
                  console.warn(`[EnumsService] Falha ao carregar enum '${enumNames[index]}', usando valores padrão.`);
              }
          });

          console.info('[EnumsService] Enums carregados do backend (com falhas isoladas, se houver).');
      } catch (error) {
          console.error('[EnumsService] Erro inesperado ao carregar enums.', error);
      }
    }

}
